export const AUTH_EVENT_QUERY_PARAM = 'auth_event';
export const AUTH_METHOD_QUERY_PARAM = 'auth_method';
export const PENDING_AUTH_REDIRECT_STORAGE_KEY = 'pending_auth_redirect';

const AUTH_REDIRECT_EVENTS = ['login', 'sign_up'] as const;
const AUTH_REDIRECT_METHODS = ['google', 'facebook', 'discord'] as const;

export type TAuthRedirectEvent = (typeof AUTH_REDIRECT_EVENTS)[number];
export type TAuthRedirectMethod = (typeof AUTH_REDIRECT_METHODS)[number];

export type TAuthRedirectPayload = {
  authEvent: TAuthRedirectEvent;
  authMethod: TAuthRedirectMethod;
};

type TStoredPendingAuthRedirect = {
  payload: TAuthRedirectPayload;
  mfaPendingCookieValue: string;
};

function isAuthRedirectEvent(
  value: string | null,
): value is TAuthRedirectEvent {
  return AUTH_REDIRECT_EVENTS.includes(value as TAuthRedirectEvent);
}

function isAuthRedirectMethod(
  value: string | null,
): value is TAuthRedirectMethod {
  return AUTH_REDIRECT_METHODS.includes(value as TAuthRedirectMethod);
}

function isAuthRedirectPayload(
  payload: unknown,
): payload is TAuthRedirectPayload {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const candidate = payload as {
    authEvent?: unknown;
    authMethod?: unknown;
  };

  return (
    typeof candidate.authEvent === 'string' &&
    isAuthRedirectEvent(candidate.authEvent) &&
    typeof candidate.authMethod === 'string' &&
    isAuthRedirectMethod(candidate.authMethod)
  );
}

function isStoredPendingAuthRedirect(
  payload: unknown,
): payload is TStoredPendingAuthRedirect {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const candidate = payload as {
    payload?: unknown;
    mfaPendingCookieValue?: unknown;
  };

  return (
    isAuthRedirectPayload(candidate.payload) &&
    typeof candidate.mfaPendingCookieValue === 'string' &&
    candidate.mfaPendingCookieValue.length > 0
  );
}

export function parseAuthRedirectParams(
  searchParams: globalThis.URLSearchParams,
): TAuthRedirectPayload | null {
  const authEvent = searchParams.get(AUTH_EVENT_QUERY_PARAM);
  const authMethod = searchParams.get(AUTH_METHOD_QUERY_PARAM);

  if (!isAuthRedirectEvent(authEvent) || !isAuthRedirectMethod(authMethod)) {
    return null;
  }

  return {
    authEvent,
    authMethod,
  };
}

export function hasAuthRedirectParams(
  searchParams: globalThis.URLSearchParams,
): boolean {
  return (
    searchParams.has(AUTH_EVENT_QUERY_PARAM) ||
    searchParams.has(AUTH_METHOD_QUERY_PARAM)
  );
}

export function stripAuthRedirectParams(url: URL): string {
  const strippedUrl = new URL(url.toString());

  strippedUrl.searchParams.delete(AUTH_EVENT_QUERY_PARAM);
  strippedUrl.searchParams.delete(AUTH_METHOD_QUERY_PARAM);

  const search = strippedUrl.searchParams.toString();
  return `${strippedUrl.pathname}${search ? `?${search}` : ''}${strippedUrl.hash}`;
}

export function stashPendingAuthRedirect(
  payload: TAuthRedirectPayload,
  mfaPendingCookieValue: string,
): void {
  window.sessionStorage.setItem(
    PENDING_AUTH_REDIRECT_STORAGE_KEY,
    JSON.stringify({ payload, mfaPendingCookieValue }),
  );
}

export function consumePendingAuthRedirect(
  currentMfaPendingCookieValue: string | null,
): TAuthRedirectPayload | null {
  const storedPayload = window.sessionStorage.getItem(
    PENDING_AUTH_REDIRECT_STORAGE_KEY,
  );

  if (!storedPayload) {
    return null;
  }

  window.sessionStorage.removeItem(PENDING_AUTH_REDIRECT_STORAGE_KEY);

  try {
    const parsedPayload = JSON.parse(storedPayload) as unknown;
    if (!isStoredPendingAuthRedirect(parsedPayload)) {
      return null;
    }

    if (parsedPayload.mfaPendingCookieValue !== currentMfaPendingCookieValue) {
      return null;
    }

    return parsedPayload.payload;
  } catch {
    return null;
  }
}
